import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
} from "@material-ui/core";
import {
    cancelAppointment,
    createAppointment,
    getClientAppointments,
    updateAppointment,
} from "../../../../api/appointment";
import { alertError, alertSuccess } from "../../../../utils/logger";
import {
    getClientGroupTherapists,
    getNextIncompleteBlock,
    getTherapistForBlock,
    therapistLabel,
} from "../../../../_metronic/_assets/js/components/user.utils";
import {
    isGoogleCalendarSyncExpired,
    isGoogleCalendarTokenExpiredError,
    promptReconnectGoogleCalendar,
} from "../../../../utils/googleCalendarAuth";
import AvailabilityGrid from "./AvailabilityGrid";

function durationFromRange(start, end) {
    if (!start || !end) return 60;
    const minutes = Math.round((new Date(end) - new Date(start)) / 60000);
    return minutes > 0 ? minutes : 60;
}

function formatWhen(iso) {
    if (!iso) return "";
    return new Intl.DateTimeFormat("es-ES", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(new Date(iso));
}

function emptyForm(client) {
    const block = getNextIncompleteBlock(client);
    const therapist = getTherapistForBlock(client, block);
    return {
        therapistId: therapist?._id || "",
        block,
        start: "",
        durationMinutes: 60,
    };
}

export default function AppointmentDialog({ open, client, onClose }) {
    const history = useHistory();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm(client));
    const [availabilityKey, setAvailabilityKey] = useState(0);
    const reauthPromptedRef = useRef(false);

    const therapists = getClientGroupTherapists(client);

    useEffect(() => {
        if (!open || !client?._id) return undefined;
        reauthPromptedRef.current = false;
        setEditingId(null);
        setForm(emptyForm(client));
        loadAppointments();
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, client?._id]);

    function maybePromptGoogleReconnect(response) {
        if (!isGoogleCalendarSyncExpired(response) || reauthPromptedRef.current) return;
        reauthPromptedRef.current = true;
        promptReconnectGoogleCalendar(history);
    }

    function loadAppointments() {
        setLoading(true);
        getClientAppointments(client._id)
            .then((res) => {
                setAppointments(Array.isArray(res.data) ? res.data : []);
                maybePromptGoogleReconnect(res);
            })
            .catch((error) => {
                if (isGoogleCalendarTokenExpiredError(error)) {
                    maybePromptGoogleReconnect({
                        headers: { "x-google-calendar-sync": "expired" },
                    });
                }
                alertError({
                    error,
                    customMessage: "No se han podido cargar las citas de Google Calendar.",
                });
            })
            .finally(() => setLoading(false));
    }

    function handleBlockChange(block) {
        const nextBlock = Number(block);
        const therapist = getTherapistForBlock(client, nextBlock);
        setForm((prev) => {
            const therapistId = therapist?._id || prev.therapistId;
            return {
                ...prev,
                block: nextBlock,
                therapistId,
                start: therapistId === prev.therapistId ? prev.start : "",
            };
        });
    }

    function startEdit(appointment) {
        setEditingId(appointment._id);
        setForm({
            therapistId: appointment.therapist?._id || appointment.therapist || "",
            block: appointment.block || getNextIncompleteBlock(client),
            start: appointment.start ? new Date(appointment.start).toISOString() : "",
            durationMinutes: durationFromRange(appointment.start, appointment.end),
        });
    }

    function resetForm() {
        setEditingId(null);
        setForm(emptyForm(client));
    }

    function handleSave() {
        if (!form.therapistId || !form.start) {
            alertError({
                error: null,
                customMessage: "Indica el terapeuta y elige una franja libre.",
            });
            return;
        }

        const payload = {
            clientId: client._id,
            therapistId: form.therapistId,
            block: form.block,
            start: form.start,
            durationMinutes: Number(form.durationMinutes) || 60,
        };

        setSaving(true);
        const request = editingId
            ? updateAppointment(editingId, payload)
            : createAppointment(payload);

        request
            .then(() => {
                alertSuccess({
                    title: "Éxito!",
                    customMessage: editingId
                        ? "Cita actualizada en Google Calendar."
                        : "Cita fijada en Google Calendar.",
                });
                resetForm();
                loadAppointments();
                setAvailabilityKey((key) => key + 1);
            })
            .catch((error) => {
                alertError({
                    error,
                    customMessage: "No se ha podido guardar la cita en Google Calendar.",
                });
            })
            .finally(() => setSaving(false));
    }

    function handleCancel(appointment) {
        if (!window.confirm("¿Cancelar esta cita en Google Calendar?")) return;
        setSaving(true);
        cancelAppointment(appointment._id)
            .then(() => {
                alertSuccess({
                    title: "Éxito!",
                    customMessage: "Cita cancelada en Google Calendar.",
                });
                if (editingId === appointment._id) resetForm();
                loadAppointments();
                setAvailabilityKey((key) => key + 1);
            })
            .catch((error) => {
                alertError({
                    error,
                    customMessage: "No se ha podido cancelar la cita.",
                });
            })
            .finally(() => setSaving(false));
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {`Gestión de citas${client ? ` · ${client.fullName}` : ""}`}
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <p>Cargando citas de Google Calendar...</p>
                ) : appointments.length === 0 ? (
                    <p>No hay citas futuras. Puedes fijar una ahora.</p>
                ) : (
                    <div style={{ marginBottom: 16 }}>
                        {appointments.map((appointment) => (
                            <div
                                key={appointment._id}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "8px 0",
                                    borderBottom: "1px solid #eee",
                                }}
                            >
                                <div>
                                    <div>
                                        <strong>{formatWhen(appointment.start)}</strong>
                                    </div>
                                    <div style={{ fontSize: "0.85rem", color: "#555" }}>
                                        {therapistLabel(appointment.therapist)}
                                        {appointment.block ? ` · Bloque ${appointment.block}` : ""}
                                        {appointment.source === "google" ? " · Reservada por el cliente" : ""}
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <Button
                                        size="small"
                                        color="primary"
                                        disabled={saving}
                                        onClick={() => startEdit(appointment)}
                                    >
                                        Editar
                                    </Button>
                                    <Button
                                        size="small"
                                        color="secondary"
                                        disabled={saving}
                                        onClick={() => handleCancel(appointment)}
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <TextField
                    select
                    label="Terapeuta"
                    value={form.therapistId}
                    onChange={(event) => setForm({ ...form, therapistId: event.target.value, start: "" })}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                >
                    {therapists.map((therapist) => (
                        <MenuItem key={therapist._id} value={therapist._id}>
                            {therapistLabel(therapist)}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Bloque"
                    value={form.block}
                    onChange={(event) => handleBlockChange(event.target.value)}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                >
                    {[1, 2, 3].map((block) => (
                        <MenuItem key={block} value={block}>
                            Bloque {block}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Duración (minutos)"
                    type="number"
                    value={form.durationMinutes}
                    onChange={(event) => setForm({ ...form, durationMinutes: event.target.value })}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    inputProps={{ min: 15, max: 480, step: 5 }}
                />
                <AvailabilityGrid
                    clientId={client?._id}
                    therapistId={form.therapistId}
                    durationMinutes={form.durationMinutes}
                    excludeAppointmentId={editingId}
                    refreshKey={availabilityKey}
                    value={form.start}
                    onChange={(start) => setForm((prev) => ({ ...prev, start }))}
                />
            </DialogContent>
            <DialogActions>
                {editingId && (
                    <Button onClick={resetForm} disabled={saving}>
                        Nueva cita
                    </Button>
                )}
                <Button onClick={onClose} color="secondary" disabled={saving}>
                    Cerrar
                </Button>
                <Button onClick={handleSave} color="primary" disabled={saving}>
                    {editingId ? "Guardar cambios" : "Fijar cita"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
